import { Link } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEOHead from '../../components/SEOHead';
import { getEssay } from '../../data/essays';
import { ArrowLeft } from 'lucide-react';

const essay = getEssay('certainty-is-now-free')!;

const CertaintyIsNowFree = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": essay.title,
    "description": essay.subtitle,
    "datePublished": essay.date,
    "dateModified": essay.dateModified ?? essay.date,
    "author": {
      "@type": "Person",
      "name": essay.author,
      "url": "https://www.linkedin.com/in/kent-frazier/",
    },
    "publisher": {
      "@type": "Organization",
      "name": "ParadoxEdge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.paradoxedge.com/lovable-uploads/180210a9-9a73-4038-a36b-f373598ef48a.png",
      },
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://www.paradoxedge.com/essays/${essay.slug}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${essay.title} | Essays`}
        description={essay.subtitle}
        canonicalUrl={`/essays/${essay.slug}`}
        ogType="article"
        ogImage={essay.ogImage}
        ogImageAlt="Certainty is now free: an essay by Kent H. Frazier"
        publishedTime={essay.date}
        modifiedTime={essay.dateModified ?? essay.date}
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-background flex flex-col">
        <Navigation />
        <main className="flex-1">
          <article className="py-20 sm:py-28">
            <div className="container-mobile max-w-[42rem]">
              {/* Back link */}
              <Link
                to="/essays"
                onClick={() => window.scrollTo(0, 0)}
                className="inline-flex items-center gap-2 text-sm text-foreground/90 hover:text-primary transition-colors mb-10"
              >
                <ArrowLeft className="w-4 h-4" />
                All essays
              </Link>

              {/* Header */}
              <header className="mb-12 sm:mb-16">
                <p className="text-xs sm:text-sm tracking-[0.25em] uppercase text-primary font-semibold mb-6">
                  Essay
                </p>
                <h1 className="mb-6">{essay.title}</h1>
                <p className="font-display text-xl sm:text-2xl text-foreground/95 leading-snug mb-8 italic">
                  {essay.subtitle}
                </p>
                <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground border-t border-border pt-6">
                  <span className="font-semibold text-foreground">By {essay.author}</span>
                  <span aria-hidden="true">·</span>
                  <time dateTime={essay.date}>{essay.dateDisplay}</time>
                  <span aria-hidden="true">·</span>
                  <span>{essay.readingMinutes} min read</span>
                </div>
              </header>

              {/* Body */}
              <div className="essay-prose">
                <p>Talking with a long-time client and Global Forward Deployment Engineering leader this week, I learned that the language “forward deployed” is becoming hollowed out by overuse, and people are now using the word “frontier” to describe engineering in the context of AI Business Transformation.</p>

                <p>The edge, I said.</p>

                <p>The edge, he said.</p>

                <p>Yes.</p>

                <p>Forward deployed presumes a center, or “knowing place” exists somewhere.</p>

                <p>You are sent from somewhere, carrying that somewhere’s intention and knowing, delivering something that already exists into a context that may be new to you and your client, but known to someone, somewhere.</p>

                <p>Let’s look at the frontier.</p>

                <p>A frontier gets settled.</p>

                <p>We land. Plant something. It becomes the center for whoever comes next.</p>

                <p>The edge moves and reorganizes every time we learn something. It calls us to attend to our relationship with the unknown, over and over, rather than a settled location.</p>

                <p>Nobody gets to finish arriving.</p>

                <p>We are standing at the threshold of the unknown unknowns.</p>

                <p>In this position, uncertainty may be uncomfortable, but certainty is absurd.</p>

                <p>Voltaire said it first, in a letter written in 1770.</p>

                <p>The machine now delivers certainty on demand.</p>

                <p>Fluent. Confident. Instant. Free.</p>

                <p>It commoditizes knowing.</p>

                {/* Section break */}
                <div className="not-prose my-16 sm:my-20 flex items-center justify-center gap-6">
                  <span className="h-px w-20 bg-primary/30" />
                  <span className="inline-block w-2 h-2 bg-primary/50 rotate-45" />
                  <span className="h-px w-20 bg-primary/30" />
                </div>

                <p>Working at the edge means knowing is not available.</p>

                <p>What’s painful is that our knowing is our identity, status, and the currency we have been paid for since our first jobs.</p>

<p>Not knowing arrives as a threat in the nervous system well before it reaches the mind. Having an understanding of this does not change our involuntary reactions under that intensely felt pressure of not knowing.</p>

<p>Standing in the position of knowing closes off our opportunity to learn from the unknown unknowns.</p>

                <p>The models know and everyone buys the same models.</p>

                <p>The precious resource is the human who can live into the questions this moment is asking of us, when an immediate and plausible answer is a prompt away.</p>

                <p>I have no idea what you will think or how you will feel when reading this.</p>

                <p>And it would be absurd for me to presume I know what you will think and how you will feel and make meaning from this piece.</p>

                <p>That’s uncomfortable for me every time I write, offering my own voice to be heard and considered.</p>

                <p>I built something new to meet this moment.</p>

                <p>I don't know if it's right.</p>

                <p>My family's livelihood is riding on it.</p>

                <p>What happens when you don’t know?</p>

                <p>Who do you become when it’s not possible to know the answer until you are living it?</p>
              </div>

              {/* Author block */}
              <aside className="mt-20 pt-10 border-t border-border">
                <p className="text-xs tracking-[0.25em] uppercase text-primary font-semibold mb-4">
                  About the author
                </p>
                <p className="text-foreground leading-relaxed">
                  Kent Frazier is the founder of ParadoxEdge. A former HR executive and Certified Integral Master Coach, he has spent three decades inside the work of leadership, partnering with senior leaders to develop the inner capacities and outer capabilities that meet the moment.
                </p>
                <div className="mt-8">
                  <a
                    href="https://calendly.com/kentfrazier/30-minutes"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground px-6 py-3 text-sm font-semibold rounded-full transition-all duration-300"
                  >
                    Begin a Conversation
                  </a>
                </div>
              </aside>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default CertaintyIsNowFree;
